import { Dispatch, SetStateAction } from 'react';
import { authToken, get, post, userProfile } from 'scripts';
import { API } from '../data/30pay.api';
import { AuthStatus, Tokens, TwoFaQrCode, UserData } from '../interfaces';
import { setAuth, setUserData } from '../redux/actions';
import { AnyAction } from 'redux';

export const _sendCode = async (
    setLoading: Dispatch<SetStateAction<boolean>>,
    data: { phoneNumber: string; forPasswordCheck: boolean },
    onComplete: (isRegister: boolean, hasPassword: boolean) => void,
) => {
    setLoading(true);
    await post<{ isRegister: boolean; hasPassword: boolean }>(API.general.sendCode, data)
        .then((response) => {
            const { isRegister, hasPassword } = response.value.value;
            onComplete(isRegister, hasPassword);
        })
        .catch((error) => {
            console.error(error);
        })
        .finally(() => setLoading(false));
};

export const _verifyPwOrCode = async (
    setLoading: Dispatch<SetStateAction<boolean>>,
    data: {
        phoneNumber?: string;
        code?: string;
        referral?: string;
        password?: string;
        forTelegram?: boolean;
    },
    onComplete: (isTwoFactorEnabled: boolean, accessToken?: string) => void,
) => {
    setLoading(true);
    await post<Tokens>(API.general.verifyPwOrOtp, data)
        .then((response) => {
            !response.value.value.isTwoFactorEnabled && authToken.set(response.value.value);
            onComplete(response.value.value.isTwoFactorEnabled!, response.value.value.accessToken);
        })
        .catch((e) => setLoading(false));
};

export const _signInVerify2Fa = async (
    setLoading: Dispatch<SetStateAction<boolean>>,
    data: {
        phoneNumber?: string;
        code?: string;
        referral?: string;
        password?: string;
        forTelegram?: boolean;
    },
    onComplete: () => void,
) => {
    setLoading(true);
    await post<Tokens>(API.general.signInVerify2Fa, data)
        .then((response) => {
            authToken.set(response.value.value);
            onComplete();
        })
        .catch((e) => setLoading(false));
};

export const _setNewPassword = async (
    setLoading: Dispatch<SetStateAction<boolean>>,
    data: {
        password: string;
        repeatPassword: string;
    },
    onComplete: (e?: boolean) => void,
) => {
    setLoading(true);
    await post<Tokens>(API.general.setNewPassword, data)
        .then((response) => {
            setLoading(false);
            authToken.set(response.value.value);
            onComplete();
        })
        .catch((e) => setLoading(false));
};

export const _forgotPassword = async (
    setLoading: Dispatch<SetStateAction<boolean>>,
    data: { phoneNumber: string | undefined },
    onComplete: () => void,
) => {
    setLoading(true);
    await post(API.general.forgotPassword, data)
        .then((response) => {
            setLoading(false);
            onComplete();
        })
        .catch((e) => setLoading(false));
};

export const _verifyForgetPassword = async (
    setLoading: Dispatch<SetStateAction<boolean>>,
    data: { phoneNumber: string | undefined; code?: string },
    onComplete: (accessToken: string) => void,
) => {
    setLoading(true);
    await post<{ accessToken: string }>(API.general.verifyForgetPassword, data)
        .then((response) => {
            setLoading(false);
            onComplete(response.value.value.accessToken);
        })
        .catch((e) => setLoading(false));
};

export const _twoFaQrCode = async (
    setLoading: Dispatch<SetStateAction<boolean>>,
    setData: Dispatch<SetStateAction<TwoFaQrCode | undefined>>,
    onComplete: () => void,
) => {
    setLoading(true);
    await get<TwoFaQrCode>(API.general.twoFaQrCode)
        .then((response) => {
            setLoading(false);
            setData(response.value.value);
            onComplete();
        })
        .catch((e) => setLoading(false));
};

export const _setup2Fa = async (
    setLoading: Dispatch<SetStateAction<boolean>>,
    data: { code: string; type: number },
    onComplete: () => void,
) => {
    setLoading(true);
    await post(API.general.setup2Fa, data)
        .then((response) => {
            onComplete();
        })
        .catch((e) => setLoading(false));
};

export const _disable2Fa = async (
    setLoading: Dispatch<SetStateAction<boolean>>,
    data: { code: string },
    onComplete: () => void,
) => {
    setLoading(true);
    await post(API.general.disable2Fa, data)
        .then((response) => {
            setLoading(false);
            onComplete();
        })
        .catch((e) => setLoading(false));
};

export const _getProfile = (dispatch: Dispatch<AnyAction>) => {
    return new Promise((resolve) => {
        if (!authToken.get()) {
            dispatch(setAuth(AuthStatus.inValid));
            resolve(true);
        } else {
            get<UserData>(API.general.profile)
                .then((res) => {
                    const userData = res.value.value;
                    userProfile.set(userData);
                    dispatch(setUserData(userData));
                    dispatch(setAuth(AuthStatus.valid));
                })
                .catch(({ status }) => {
                    if (status >= 500) console.log('Error');
                    else if (status >= 400) {
                        authToken.remove();
                        userProfile.remove();
                        dispatch(setAuth(AuthStatus.inValid));
                    } else console.log('Error');
                })
                .finally(() => resolve(true));
        }
    });
};

export const _editPhoneNumber = async (
    setLoading: Dispatch<SetStateAction<boolean>>,
    data: { newPhoneNumber: string },
    onComplete: () => void,
) => {
    setLoading(true);
    await post(API.general.editPhoneNumber, data)
        .then((response) => {
            setLoading(false);
            onComplete();
        })
        .catch((e) => setLoading(false));
};

export const _verifyNewPhoneNumber = async (
    setLoading: Dispatch<SetStateAction<boolean>>,
    data: { otpOldPhoneNumber: string; newPhoneNumber: string },
    onComplete: () => void,
) => {
    setLoading(true);
    await post(API.general.verifyNewPhoneNumber, data)
        .then((response) => {
            setLoading(false);
            onComplete();
        })
        .catch((e) => setLoading(false));
};
